// src/contexts/RecipeContext.tsx

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  ReactNode,
} from 'react'
import axios from 'axios'
import config from '../config'
import { useAuth } from './AuthContext'

interface Ingredient {
  name: string
  quantity: string | null
  unit: string | null
  dietary_info?: {
    vegan?: boolean
    vegetarian?: boolean
    pescatarian?: boolean
    gluten_free?: boolean
    dairy_free?: boolean
    nut_free?: boolean
    egg_free?: boolean
    soy_free?: boolean
    shellfish_free?: boolean
    fish_free?: boolean
    sesame_free?: boolean
    low_fodmap?: boolean
    keto?: boolean
    paleo?: boolean
    atkins?: boolean
    corn_free?: boolean
    mustard_free?: boolean
    confidence_score?: number
    needs_human_review?: boolean
    human_reviewed?: boolean
    prediction_date?: string
    normalized_name?: string
    original_text?: string
    nutritional_profile?: {
      confirmed?: boolean
      protein_per_100g?: string
      calories_per_100g?: string
      carbohydrates_per_100g?: string
      fiber_per_100g?: string
      sugar_per_100g?: string
      saturated_fat_per_100g?: string
      trans_fat_per_100g?: string
      omega3_per_100g?: string
      sodium_per_100g?: string
      potassium_per_100g?: string
      iron_per_100g?: string
      calcium_per_100g?: string
      magnesium_per_100g?: string
      phosphorus_per_100g?: string
      cholesterol_per_100g?: string
    }
  }
}

interface Instruction {
  order: number
  text: string
}

export interface RecipePhoto {
  id: string
  url: string
  caption?: string | null
  is_primary: boolean
  position: number
}

export interface RecipeData {
  id: string
  title: string
  token_count: number
  ingredients: Ingredient[]
  instructions: Instruction[]
  created_at?: string
  image?: string | null
  source_url?: string
  type?: 'saved' | 'created' | 'variant'
  base_recipe_id?: string // For variants
  base_recipe_title?: string // Title of the original recipe for variants
  photos?: RecipePhoto[]
  dietary_info?: {
    vegan?: boolean
    vegetarian?: boolean
    pescatarian?: boolean
    gluten_free?: boolean
    dairy_free?: boolean
    nut_free?: boolean
    egg_free?: boolean
    soy_free?: boolean
    shellfish_free?: boolean
    fish_free?: boolean
    sesame_free?: boolean
    low_fodmap?: boolean
    keto?: boolean
    paleo?: boolean
    atkins?: boolean
    corn_free?: boolean
    mustard_free?: boolean
    overall_confidence?: number
    needs_human_review?: boolean
    [key: string]: any
  }
  dietary_summary?: {
    suitable_diets?: string[]
    unsuitable_for?: string[]
    confidence_level?: string
    needs_review?: boolean
  }
}

// Recipe Variant interfaces
interface VariantIngredient {
  id?: string
  name: string
  quantity: string | null
  unit: string | null
  order: number
  base_ingredient_id?: string | null
}

interface VariantInstruction {
  id?: string
  text: string
  order: number
  base_instruction_id?: string | null
}

export interface RecipeVariant {
  id: string
  title: string
  base_recipe_id: string
  base_recipe_title?: string // Title of the original recipe
  user_id: string
  created_at: string
  variant_ingredients: VariantIngredient[]
  variant_instructions: VariantInstruction[]
}

// Comparison interfaces
interface IngredientComparison {
  original: Ingredient | null
  modified: VariantIngredient | null
  status: 'added' | 'removed' | 'modified' | 'unchanged'
}

interface InstructionComparison {
  original: Instruction | null
  modified: VariantInstruction | null
  status: 'added' | 'removed' | 'modified' | 'unchanged'
}

export interface RecipeVariantComparison {
  variant_id: string
  variant_title: string
  base_recipe_id: string
  base_recipe_title: string
  ingredient_changes: IngredientComparison[]
  instruction_changes: InstructionComparison[]
  total_changes: number
  ingredients_added: number
  ingredients_removed: number
  ingredients_modified: number
  instructions_added: number
  instructions_removed: number
  instructions_modified: number
}

// Grocery List interfaces
export interface GroceryList {
  id: string
  user_id: string
  name: string
  created_at: string
  updated_at?: string
}

export interface GroceryListItem {
  id: string
  list_id: string
  name: string
  quantity_value?: number
  quantity_unit?: string
  note?: string
  is_checked: boolean
  created_at: string
  updated_at?: string
}

export interface GroceryListItemContribution {
  id: string
  list_item_id: string
  recipe_id: string
  recipe_ingredient_id?: string
  quantity_value?: number
  quantity_unit?: string
  created_at: string
}

export interface GroceryListWithItems extends GroceryList {
  items: GroceryListItem[]
}

export interface AddRecipeRequest {
  recipe_id: string
  servings?: number
}

export interface RemoveRecipeRequest {
  recipe_id: string
}

export interface GroceryListSummary {
  id: string
  name: string
  created_at: string
  updated_at?: string
  total_items: number
  checked_items: number
  completion_percentage: number
  last_recipe_added?: string
}

export interface CopyGroceryListRequest {
  new_name: string
  copy_checked_status?: boolean
}

interface PaginatedRecipes<T> {
  items: T[]
  total_count: number
  total_pages: number
  page: number
  page_size: number
}

interface RecipeContextType {
  url: string
  setUrl: (url: string) => void
  recipe: RecipeData | null
  isLoading: boolean
  fetchRecipe: () => Promise<void>

  savedRecipes: RecipeData[]
  fetchSaved: () => Promise<void>
  save: (recipeId: string) => Promise<void>
  unsave: (recipeId: string) => Promise<void>
  
  // Unified user recipes (saved + created + variants)
  allUserRecipes: RecipeData[]
  fetchAllUserRecipes: () => Promise<void>

  // Recipe API methods
  getRecipeById: (recipeId: string) => Promise<RecipeData | null>
  listUserRecipes: (userId: string) => Promise<RecipeData[]>
  createRecipe: (recipe: Partial<RecipeData> & { ingredients: Ingredient[]; instructions: Instruction[]; user_id?: string }) => Promise<RecipeData | null>
  updateRecipe: (recipeId: string, update: Partial<RecipeData> & { ingredients: Ingredient[]; instructions: Instruction[]; user_id?: string }) => Promise<RecipeData | null>
  deleteRecipe: (recipeId: string) => Promise<void>
  getCreatedRecipesByUser: (userId: string) => Promise<RecipeData[]>

  // Search API
  searchRecipes: (params: {
    q?: string
    ingredients?: string[]
    match?: 'any' | 'all'
    source_url?: string
    page?: number
    page_size?: number
  }) => Promise<PaginatedRecipes<RecipeData>>

  // Photo API
  presignRecipePhoto: (recipeId: string, args: { content_type: string; ext?: string }) => Promise<{ upload_url: string; object_key: string; public_url: string }>
  addRecipePhoto: (recipeId: string, args: { url: string; caption?: string; is_primary?: boolean; position?: number }) => Promise<RecipePhoto | null>

  // Recipe Variant API methods
  createVariantFromRecipe: (recipeId: string, userId: string, customTitle?: string) => Promise<RecipeVariant | null>
  getUserVariants: (userId: string) => Promise<RecipeVariant[]>
  getVariant: (variantId: string) => Promise<RecipeVariant | null>
  updateVariant: (variantId: string, update: { 
    title: string; 
    base_recipe_id: string; 
    user_id: string; 
    variant_ingredients: VariantIngredient[]; 
    variant_instructions: VariantInstruction[] 
  }) => Promise<RecipeVariant | null>
  deleteVariant: (variantId: string) => Promise<void>
  compareVariantToBase: (variantId: string) => Promise<RecipeVariantComparison | null>

  // Grocery List API methods
  createGroceryList: (name: string) => Promise<GroceryList | null>
  getGroceryLists: () => Promise<GroceryList[]>
  getGroceryList: (listId: string) => Promise<GroceryList | null>
  getGroceryListWithItems: (listId: string) => Promise<GroceryListWithItems | null>
  updateGroceryList: (listId: string, name: string) => Promise<GroceryList | null>
  deleteGroceryList: (listId: string) => Promise<void>
  
  // Grocery List Item methods
  addRecipeToGroceryList: (listId: string, recipeId: string, servings?: number) => Promise<void>
  removeRecipeFromGroceryList: (listId: string, recipeId: string) => Promise<void>
  addManualGroceryItem: (listId: string, item: { name: string; quantity_value?: number; quantity_unit?: string; note?: string }) => Promise<GroceryListItem | null>
  updateGroceryItem: (listId: string, itemId: string, updates: { name?: string; quantity_value?: number; quantity_unit?: string; note?: string; is_checked?: boolean }) => Promise<GroceryListItem | null>
  deleteGroceryItem: (listId: string, itemId: string) => Promise<void>
  
  // Grocery List History methods
  getGroceryListsSummary: () => Promise<GroceryListSummary[]>
  copyGroceryList: (listId: string, newName: string, copyCheckedStatus?: boolean) => Promise<GroceryList | null>
}

const RecipeContext = createContext<RecipeContextType | null>(null)

export const useRecipe = (): RecipeContextType => {
  const ctx = useContext(RecipeContext)
  if (!ctx) throw new Error('useRecipe must be used within a RecipeProvider')
  return ctx
}

interface RecipeProviderProps {
  children: ReactNode
}

export const RecipeProvider: React.FC<RecipeProviderProps> = ({ children }) => {
  const { user } = useAuth()
  const [url, setUrl] = useState('')
  const [recipe, setRecipe] = useState<RecipeData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [savedRecipes, setSavedRecipes] = useState<RecipeData[]>([])
  const [allUserRecipes, setAllUserRecipes] = useState<RecipeData[]>([])

  interface PaginatedRecipes<T> {
    items: T[]
    total_count: number
    total_pages: number
    page: number
    page_size: number
  }

  // Build an Authorization header object, or undefined if not logged in
  const authHeader = useMemo(() => {
    if (!user) return undefined
    const headerValue = `${user.tokenType.charAt(0).toUpperCase()}${user.tokenType.slice(1)} ${user.accessToken}`
    return { Authorization: headerValue }
  }, [user])

  // 1️⃣ Fetch a parsed recipe (no auth required)
  const fetchRecipe = useCallback(async () => {
    setIsLoading(true)
    try {
      const { data } = await axios.post<RecipeData>(
        `${config.API_BASE_URL}/parse/recipe`,
        { url }
      )
      setRecipe(data)
    } catch (err) {
      console.error('Error fetching recipe:', err)
      setRecipe(null)
    } finally {
      setIsLoading(false)
    }
  }, [url])

  // 2️⃣ Load list of saved recipes
  const fetchSaved = useCallback(async () => {
    if (!authHeader) return
    try {
      const { data } = await axios.get<RecipeData[]>(
        `${config.API_BASE_URL}/saved-recipes`,
        { headers: authHeader }
      )
      setSavedRecipes(data)
    } catch (err) {
      console.error('Error loading saved recipes:', err)
    }
  }, [authHeader])

  // 2.5️⃣ Load all user recipes (saved + created + variants)
  const fetchAllUserRecipes = useCallback(async () => {
    if (!authHeader) return
    try {
      const { data } = await axios.get<RecipeData[]>(
        `${config.API_BASE_URL}/user/my-recipes`,
        { headers: authHeader }
      )
      setAllUserRecipes(data)
    } catch (err) {
      console.error('Error loading all user recipes:', err)
    }
  }, [authHeader])

  // 3️⃣ Save then refresh
  const save = useCallback(
    async (recipeId: string) => {
      console.log('Saving recipe with ID:', recipeId)
      console.log('Auth header:', authHeader)
      if (!authHeader) return
      try {
        await axios.post(
          `${config.API_BASE_URL}/saved-recipes`,
          { recipe_id: recipeId },
          { headers: authHeader }
        )
        await fetchSaved()
      } catch (err) {
        console.error('Error saving recipe:', err)
      }
    },
    [authHeader, fetchSaved]
  )

  // 4️⃣ Unsave then refresh
  const unsave = useCallback(
    async (recipeId: string) => {
      if (!authHeader) return
      try {
        await axios.delete(
          `${config.API_BASE_URL}/saved-recipes/${recipeId}`,
          { headers: authHeader }
        )
        await fetchSaved()
      } catch (err) {
        console.error('Error unsaving recipe:', err)
      }
    },
    [authHeader, fetchSaved]
  )

  // Get a single recipe by ID
  const getRecipeById = useCallback(
    async (recipeId: string): Promise<RecipeData | null> => {
      try {
        const { data } = await axios.get<RecipeData>(
          `${config.API_BASE_URL}/api/recipes/${recipeId}`
        )
        return data
      } catch (err) {
        console.error('Error fetching recipe by ID:', err)
        return null
      }
    },
    []
  )

  // List all recipes for a user
  const listUserRecipes = useCallback(
    async (userId: string): Promise<RecipeData[]> => {
      try {
        const { data } = await axios.get<RecipeData[]>(
          `${config.API_BASE_URL}/api/recipes/user/${userId}`
        )
        return data
      } catch (err) {
        console.error('Error listing user recipes:', err)
        return []
      }
    },
    []
  )

  // Create a new recipe (send all data at once)
  const createRecipe = useCallback(
    async (recipe: Partial<RecipeData> & { ingredients: Ingredient[]; instructions: Instruction[]; user_id?: string }): Promise<RecipeData | null> => {
      try {
        const { data } = await axios.post<RecipeData>(
          `${config.API_BASE_URL}/api/recipes/`,
          recipe,
          authHeader ? { headers: authHeader } : undefined
        )
        return data
      } catch (err) {
        console.error('Error creating recipe:', err)
        return null
      }
    },
    [authHeader]
  )

  // Update a recipe (send all data at once)
  const updateRecipe = useCallback(
    async (recipeId: string, update: Partial<RecipeData> & { ingredients: Ingredient[]; instructions: Instruction[]; user_id?: string }): Promise<RecipeData | null> => {
      try {
        const { data } = await axios.put<RecipeData>(
          `${config.API_BASE_URL}/api/recipes/${recipeId}`,
          update,
          authHeader ? { headers: authHeader } : undefined
        )
        return data
      } catch (err) {
        console.error('Error updating recipe:', err)
        return null
      }
    },
    [authHeader]
  )

  // Delete a recipe
  const deleteRecipe = useCallback(
    async (recipeId: string): Promise<void> => {
      try {
        await axios.delete(
          `${config.API_BASE_URL}/api/recipes/${recipeId}`,
          authHeader ? { headers: authHeader } : undefined
        )
      } catch (err) {
        console.error('Error deleting recipe:', err)
      }
    },
    [authHeader]
  )

  // Fetch recipes created by a user
  const getCreatedRecipesByUser = useCallback(
    async (userId: string): Promise<RecipeData[]> => {
      try {
        console.log('API HIT')
        const { data } = await axios.get<RecipeData[]>(
          `${config.API_BASE_URL}/api/recipes/created-by/${userId}`,
          authHeader ? { headers: authHeader } : undefined
        );
        console.log('data', data)
        return data;
      } catch (err) {
        console.error('Error fetching created recipes:', err);
        return [];
      }
    },
    [authHeader]
  );

  // Search recipes by title and/or ingredients
  const searchRecipes = useCallback(
    async (params: {
      q?: string
      ingredients?: string[]
      match?: 'any' | 'all'
      source_url?: string
      page?: number
      page_size?: number
    }): Promise<PaginatedRecipes<RecipeData>> => {
      const query = new URLSearchParams()
      if (params.q) query.append('q', params.q)
      if (params.ingredients && params.ingredients.length) {
        for (const term of params.ingredients) {
          query.append('ingredients', term)
        }
      }
      if (params.match) query.append('match', params.match)
      if (params.source_url) query.append('source_url', params.source_url)
      if (params.page) query.append('page', String(params.page))
      if (params.page_size) query.append('page_size', String(params.page_size))

      const url = `${config.API_BASE_URL}/api/recipes/search?${query.toString()}`
      const { data } = await axios.get<PaginatedRecipes<RecipeData>>(url)
      return data
    },
    []
  )

  // Photo API
  const presignRecipePhoto = useCallback(
    async (recipeId: string, args: { content_type: string; ext?: string }) => {
      const { data } = await axios.post<{ upload_url: string; object_key: string; public_url: string }>(
        `${config.API_BASE_URL}/api/recipes/${recipeId}/photos/presign`,
        args
      )
      return data
    },
    []
  )

  const addRecipePhoto = useCallback(
    async (recipeId: string, args: { url: string; caption?: string; is_primary?: boolean; position?: number }) => {
      try {
        const { data } = await axios.post<RecipePhoto>(
          `${config.API_BASE_URL}/api/recipes/${recipeId}/photos`,
          { recipe_id: recipeId, ...args }
        )
        return data
      } catch (err) {
        console.error('Error adding recipe photo:', err)
        return null
      }
    },
    []
  )

  // Recipe Variant API Methods

  // Create a variant from an existing recipe (main "Customize Recipe" functionality)
  const createVariantFromRecipe = useCallback(
    async (recipeId: string, userId: string, customTitle?: string): Promise<RecipeVariant | null> => {
      if (!authHeader) return null;
      try {
        const params = new URLSearchParams({
          user_id: userId,
          ...(customTitle && { custom_title: customTitle })
        });
        const { data } = await axios.post<RecipeVariant>(
          `${config.API_BASE_URL}/recipe-variants/create-from-recipe/${recipeId}?${params}`,
          {},
          { headers: authHeader }
        );
        return data;
      } catch (err) {
        console.error('Error creating variant from recipe:', err);
        return null;
      }
    },
    [authHeader]
  );

  // Get all variants created by a user
  const getUserVariants = useCallback(
    async (userId: string): Promise<RecipeVariant[]> => {
      if (!authHeader) return [];
      try {
        const { data } = await axios.get<RecipeVariant[]>(
          `${config.API_BASE_URL}/recipe-variants/user/${userId}`,
          { headers: authHeader }
        );
        return data;
      } catch (err) {
        console.error('Error fetching user variants:', err);
        return [];
      }
    },
    [authHeader]
  );

  // Get a specific variant by ID
  const getVariant = useCallback(
    async (variantId: string): Promise<RecipeVariant | null> => {
      if (!authHeader) return null;
      try {
        const { data } = await axios.get<RecipeVariant>(
          `${config.API_BASE_URL}/recipe-variants/${variantId}`,
          { headers: authHeader }
        );
        return data;
      } catch (err) {
        console.error('Error fetching variant:', err);
        return null;
      }
    },
    [authHeader]
  );

  // Update a recipe variant
  const updateVariant = useCallback(
    async (variantId: string, update: { 
      title: string; 
      base_recipe_id: string; 
      user_id: string; 
      variant_ingredients: VariantIngredient[]; 
      variant_instructions: VariantInstruction[] 
    }): Promise<RecipeVariant | null> => {
      if (!authHeader) return null;
      try {
        const { data } = await axios.put<RecipeVariant>(
          `${config.API_BASE_URL}/recipe-variants/${variantId}`,
          update,
          { headers: authHeader }
        );
        return data;
      } catch (err) {
        console.error('Error updating variant:', err);
        return null;
      }
    },
    [authHeader]
  );

  // Delete a recipe variant
  const deleteVariant = useCallback(
    async (variantId: string): Promise<void> => {
      if (!authHeader) return;
      try {
        await axios.delete(
          `${config.API_BASE_URL}/recipe-variants/${variantId}`,
          { headers: authHeader }
        );
      } catch (err) {
        console.error('Error deleting variant:', err);
      }
    },
    [authHeader]
  );

  // Compare variant to its base recipe (for comparison UI)
  const compareVariantToBase = useCallback(
    async (variantId: string): Promise<RecipeVariantComparison | null> => {
      if (!authHeader) return null;
      try {
        const { data } = await axios.get<RecipeVariantComparison>(
          `${config.API_BASE_URL}/recipe-variants/${variantId}/compare`,
          { headers: authHeader }
        );
        return data;
      } catch (err) {
        console.error('Error comparing variant to base:', err);
        return null;
      }
    },
    [authHeader]
  );

  // Grocery List API methods
  const createGroceryList = useCallback(
    async (name: string): Promise<GroceryList | null> => {
      if (!authHeader) return null;
      try {
        const { data } = await axios.post<GroceryList>(
          `${config.API_BASE_URL}/grocery-lists`,
          { name },
          { headers: authHeader }
        );
        return data;
      } catch (err) {
        console.error('Error creating grocery list:', err);
        return null;
      }
    },
    [authHeader]
  );

  const getGroceryLists = useCallback(
    async (): Promise<GroceryList[]> => {
      if (!authHeader) return [];
      try {
        const { data } = await axios.get<GroceryList[]>(
          `${config.API_BASE_URL}/grocery-lists`,
          { headers: authHeader }
        );
        return data;
      } catch (err) {
        console.error('Error fetching grocery lists:', err);
        return [];
      }
    },
    [authHeader]
  );

  const getGroceryList = useCallback(
    async (listId: string): Promise<GroceryList | null> => {
      if (!authHeader) return null;
      try {
        const { data } = await axios.get<GroceryList>(
          `${config.API_BASE_URL}/grocery-lists/${listId}`,
          { headers: authHeader }
        );
        return data;
      } catch (err) {
        console.error('Error fetching grocery list:', err);
        return null;
      }
    },
    [authHeader]
  );

  const getGroceryListWithItems = useCallback(
    async (listId: string): Promise<GroceryListWithItems | null> => {
      if (!authHeader) return null;
      try {
        const { data } = await axios.get<GroceryListWithItems>(
          `${config.API_BASE_URL}/grocery-lists/${listId}/items`,
          { headers: authHeader }
        );
        return data;
      } catch (err) {
        console.error('Error fetching grocery list with items:', err);
        return null;
      }
    },
    [authHeader]
  );

  const updateGroceryList = useCallback(
    async (listId: string, name: string): Promise<GroceryList | null> => {
      if (!authHeader) return null;
      try {
        const { data } = await axios.put<GroceryList>(
          `${config.API_BASE_URL}/grocery-lists/${listId}`,
          { name },
          { headers: authHeader }
        );
        return data;
      } catch (err) {
        console.error('Error updating grocery list:', err);
        return null;
      }
    },
    [authHeader]
  );

  const deleteGroceryList = useCallback(
    async (listId: string): Promise<void> => {
      if (!authHeader) return;
      try {
        await axios.delete(
          `${config.API_BASE_URL}/grocery-lists/${listId}`,
          { headers: authHeader }
        );
      } catch (err) {
        console.error('Error deleting grocery list:', err);
      }
    },
    [authHeader]
  );

  // Grocery List Item methods
  const addRecipeToGroceryList = useCallback(
    async (listId: string, recipeId: string, servings?: number): Promise<void> => {
      if (!authHeader) return;
      try {
        await axios.post(
          `${config.API_BASE_URL}/grocery-lists/${listId}/add-recipe`,
          { recipe_id: recipeId, servings },
          { headers: authHeader }
        );
      } catch (err) {
        console.error('Error adding recipe to grocery list:', err);
      }
    },
    [authHeader]
  );

  const removeRecipeFromGroceryList = useCallback(
    async (listId: string, recipeId: string): Promise<void> => {
      if (!authHeader) return;
      try {
        await axios.post(
          `${config.API_BASE_URL}/grocery-lists/${listId}/remove-recipe`,
          { recipe_id: recipeId },
          { headers: authHeader }
        );
      } catch (err) {
        console.error('Error removing recipe from grocery list:', err);
      }
    },
    [authHeader]
  );

  const addManualGroceryItem = useCallback(
    async (listId: string, item: { name: string; quantity_value?: number; quantity_unit?: string; note?: string }): Promise<GroceryListItem | null> => {
      if (!authHeader) return null;
      try {
        const { data } = await axios.post<GroceryListItem>(
          `${config.API_BASE_URL}/grocery-lists/${listId}/items`,
          item,
          { headers: authHeader }
        );
        return data;
      } catch (err) {
        console.error('Error adding manual grocery item:', err);
        return null;
      }
    },
    [authHeader]
  );

  const updateGroceryItem = useCallback(
    async (listId: string, itemId: string, updates: { name?: string; quantity_value?: number; quantity_unit?: string; note?: string; is_checked?: boolean }): Promise<GroceryListItem | null> => {
      if (!authHeader) return null;
      try {
        const { data } = await axios.put<GroceryListItem>(
          `${config.API_BASE_URL}/grocery-lists/${listId}/items/${itemId}`,
          updates,
          { headers: authHeader }
        );
        return data;
      } catch (err) {
        console.error('Error updating grocery item:', err);
        return null;
      }
    },
    [authHeader]
  );

  const deleteGroceryItem = useCallback(
    async (listId: string, itemId: string): Promise<void> => {
      if (!authHeader) return;
      try {
        await axios.delete(
          `${config.API_BASE_URL}/grocery-lists/${listId}/items/${itemId}`,
          { headers: authHeader }
        );
      } catch (err) {
        console.error('Error deleting grocery item:', err);
      }
    },
    [authHeader]
  );

  // Grocery List History methods
  const getGroceryListsSummary = useCallback(
    async (): Promise<GroceryListSummary[]> => {
      if (!authHeader) return [];
      try {
        const { data } = await axios.get<GroceryListSummary[]>(
          `${config.API_BASE_URL}/grocery-lists/summary`,
          { headers: authHeader }
        );
        return data;
      } catch (err) {
        console.error('Error fetching grocery lists summary:', err);
        return [];
      }
    },
    [authHeader]
  );

  const copyGroceryList = useCallback(
    async (listId: string, newName: string, copyCheckedStatus: boolean = false): Promise<GroceryList | null> => {
      if (!authHeader) return null;
      try {
        const { data } = await axios.post<GroceryList>(
          `${config.API_BASE_URL}/grocery-lists/${listId}/copy`,
          { 
            new_name: newName,
            copy_checked_status: copyCheckedStatus 
          },
          { headers: authHeader }
        );
        return data;
      } catch (err) {
        console.error('Error copying grocery list:', err);
        return null;
      }
    },
    [authHeader]
  );

  // auto‑load saved recipes once user is set
  useEffect(() => {
    if (user) {
      fetchSaved()
    }
  }, [user, fetchSaved])

  const value = useMemo(
    () => ({
      url,
      setUrl,
      recipe,
      isLoading,
      fetchRecipe,
      savedRecipes,
      fetchSaved,
      save,
      unsave,
      allUserRecipes,
      fetchAllUserRecipes,
      getRecipeById,
      listUserRecipes,
      createRecipe,
      updateRecipe,
      deleteRecipe,
      getCreatedRecipesByUser,
      searchRecipes,
      presignRecipePhoto,
      addRecipePhoto,
      // Recipe Variant methods
      createVariantFromRecipe,
      getUserVariants,
      getVariant,
      updateVariant,
      deleteVariant,
      compareVariantToBase,
      // Grocery List methods
      createGroceryList,
      getGroceryLists,
      getGroceryList,
      getGroceryListWithItems,
      updateGroceryList,
      deleteGroceryList,
      addRecipeToGroceryList,
      removeRecipeFromGroceryList,
      addManualGroceryItem,
      updateGroceryItem,
      deleteGroceryItem,
      // Grocery List History methods
      getGroceryListsSummary,
      copyGroceryList,
    }),
    [
      url, 
      recipe, 
      isLoading, 
      savedRecipes, 
      allUserRecipes,
      fetchRecipe, 
      fetchSaved, 
      fetchAllUserRecipes,
      save, 
      unsave, 
      getRecipeById, 
      listUserRecipes, 
      createRecipe, 
      updateRecipe, 
      deleteRecipe, 
      getCreatedRecipesByUser,
      searchRecipes,
      presignRecipePhoto,
      addRecipePhoto,
      // Recipe Variant dependencies
      createVariantFromRecipe,
      getUserVariants,
      getVariant,
      updateVariant,
      deleteVariant,
      compareVariantToBase,
      // Grocery List dependencies
      createGroceryList,
      getGroceryLists,
      getGroceryList,
      getGroceryListWithItems,
      updateGroceryList,
      deleteGroceryList,
      addRecipeToGroceryList,
      removeRecipeFromGroceryList,
      addManualGroceryItem,
      updateGroceryItem,
      deleteGroceryItem,
      getGroceryListsSummary,
      copyGroceryList
    ]
  )

  return (
    <RecipeContext.Provider value={value}>
      {children}
    </RecipeContext.Provider>
  )
}